import type { BeadColor } from '../types';

export type Rgb = { r: number; g: number; b: number };

export function hexToRgb(hex: string): Rgb {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(
    normalized.length === 3
      ? normalized
          .split('')
          .map((part) => part + part)
          .join('')
      : normalized,
    16
  );

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b]
    .map((value) => Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0'))
    .join('')}`;
}

function srgbToLinear(value: number) {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

export function readableTextColor(hex: string) {
  return relativeLuminance(hex) > 0.54 ? '#172126' : '#ffffff';
}

function weightedDistance(a: Rgb, b: Rgb) {
  const redMean = (a.r + b.r) / 2;
  const red = a.r - b.r;
  const green = a.g - b.g;
  const blue = a.b - b.b;

  return Math.sqrt(
    (2 + redMean / 256) * red * red + 4 * green * green + (2 + (255 - redMean) / 256) * blue * blue
  );
}

export function findNearestPaletteColor(rgb: Rgb, colors: BeadColor[]): BeadColor {
  let nearest = colors[0];
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const color of colors) {
    if (color.id === 'transparent') continue;
    const distance = weightedDistance(rgb, hexToRgb(color.hex));
    if (distance < nearestDistance) {
      nearest = color;
      nearestDistance = distance;
    }
  }

  return nearest;
}

export function sortColorIdsByNearest(targetId: string, colors: BeadColor[]) {
  const target = colors.find((color) => color.id === targetId);
  if (!target) return colors.map((color) => color.id);

  const rgb = hexToRgb(target.hex);
  return [...colors]
    .sort((a, b) => weightedDistance(rgb, hexToRgb(a.hex)) - weightedDistance(rgb, hexToRgb(b.hex)))
    .map((color) => color.id);
}
